import { defineStore } from 'pinia';
import { useFetch } from "@/plugins/fetch";
import { useMainStore } from "@/store/MainStore";

// Utility function to convert camelCase to snake_case
const camelToSnake = (str) =>
    str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

// Utility function to convert snake_case to camelCase
const snakeToCamel = (str) =>
    str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

export const useSettingStore = defineStore({
    id: 'settings',
    state: () => ({
        darkMode: false,
        units: 'metric',
        notifyByEmail: false,
        notifyBySMS: false,
        notifyByPush: false,
        getStartedShown: true,
    }),
    actions: {
        async fetchUserSettings() {
            const fetchClient = useFetch();
            try {
                const response = await fetchClient('user/settings/', { method: 'GET' });
                // Handle wrapped API response
                const data = response?.data || response || {};
                const settings = Object.entries(data).reduce((acc, [key, value]) => {
                    acc[snakeToCamel(key)] = value;
                    return acc;
                }, {});
                this.$patch(settings);

                const mainStore = useMainStore();
                if (this.darkMode && !mainStore.darkMode) {
                    mainStore.toggleDarkMode();
                }
            } catch (error) {
                console.error("Error fetching user settings", error);
            }
        },
        async updateSetting(key, value) {
            const fetchClient = useFetch();
            try {
                const snakeKey = camelToSnake(key);
                await fetchClient('user/settings/', {
                    method: 'POST',
                    body: { [snakeKey]: value },
                    headers: { 'Content-Type': 'application/json' }
                });
                this.$patch({ [key]: value });
            } catch (error) {
                console.error(`Error updating setting ${key}`, error);
                throw error;
            }
        },
        async updateAllSettings() {
            const fetchClient = useFetch();
            try {
                const payload = {
                    dark_mode: this.darkMode,
                    units: this.units,
                    notify_by_email: this.notifyByEmail,
                    notify_by_sms: this.notifyBySMS,
                    notify_by_push: this.notifyByPush,
                };
                await fetchClient('user/settings/', {
                    method: 'POST',
                    body: payload,
                    headers: { 'Content-Type': 'application/json' }
                });
            } catch (error) {
                console.error("Error updating all settings", error);
                throw error;
            }
        },
        // Local toggles can be removed if you handle changes through a save button
        // but are kept here if you want instant-save functionality elsewhere.
        toggleDarkMode() { this.darkMode = !this.darkMode; },
        toggleMetricUnits() { this.units = this.units === 'metric' ? 'imperial' : 'metric'; },
        toggleNotifyByEmail() { this.notifyByEmail = !this.notifyByEmail; },
        toggleNotifyBySMS() { this.notifyBySMS = !this.notifyBySMS; },
        toggleNotifyByPush() { this.notifyByPush = !this.notifyByPush; },
        toggleGetStartedShown() { this.getStartedShown = !this.getStartedShown; },
    },
    getters: {
        isDarkMode: (state) => state.darkMode,
        isMetricUnits: (state) => state.units === 'metric',
        isNotifyByEmail: (state) => state.notifyByEmail,
        isNotifyBySMS: (state) => state.notifyBySMS,
        isNotifyByPush: (state) => state.notifyByPush,
        isGetStartedShown: (state) => state.getStartedShown
    },
    persist: true
});
